import { useStripe } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';

import { captureException, useLogger } from '@/logging';

/**
 * Returns the enabled one click payment provider.
 */
export const useOneClickPaymentProvider = (args: {
  country: string;
  currency: string;
}) => {
  const [supportedWallet, setSupportedWallet] = useState<
    'applePay' | 'googlePay' | null
  >(null);
  const [hasFinishedChecking, setHasFinishedChecking] = useState(false);
  const logger = useLogger();

  const stripe = useStripe();

  useEffect(() => {
    if (!stripe) {
      logger.warn('[apple pay/google pay] Stripe has not loaded yet...');
      return;
    }

    logger.info('[apple pay/google pay] checking wallet availability...');
    const paymentRequest = stripe.paymentRequest({
      country: args.country.toUpperCase(),
      currency: args.currency.toLowerCase(),
      total: {
        label: 'Apple Pay/Google Pay check',
        amount: 100,
      },
    });

    if (!paymentRequest) {
      logger.warn(
        '[apple pay/google pay] failed to create nominal payment request'
      );
      return;
    }

    paymentRequest
      .canMakePayment()
      .then((result) => {
        if (result?.applePay) {
          setSupportedWallet('applePay');
        } else if (result?.googlePay) {
          setSupportedWallet('googlePay');
        } else {
          setSupportedWallet(null);
        }
        logger.info(
          `[apple pay/google pay] can make payment check returned [${JSON.stringify(
            result
          )}]`
        );
        setHasFinishedChecking(true);
      })
      .catch((error) => {
        logger.error(
          `[apple pay/google pay] failed to check if wallet payment is available`,
          error
        );
        captureException(error);
        setSupportedWallet(null);
      });
  }, [args.country, args.currency, stripe, logger]);

  return { supportedWallet, hasFinishedChecking };
};
